"use client";

import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";
import { LottiePlayerButton } from "@/app/(public)/shared/ui/LottiePlayerButton";
import { useLottieProgress } from "@/app/(public)/shared/hooks/useLottieProgress";
import Lottie from "lottie-react";
import { useInView } from "react-intersection-observer";
import { useRef, useEffect, useState } from "react";
import { LottieRefCurrentProps } from "lottie-react";
import exclusiveRatesAnimation from "@/app/(public)/shared/assets/animations/exclusiverates.json";
import negotiationAnimation from "@/app/(public)/shared/assets/animations/negotiation.json";
import allInOneAnimation from "@/app/(public)/shared/assets/animations/all-in-one.json";

export const Feature3 = () => {
  // Exclusive rates animation refs and observers
  const exclusiveRatesLottieRef = useRef<LottieRefCurrentProps>(null);
  const [isExclusiveRatesPlaying, setIsExclusiveRatesPlaying] = useState(true);
  const { ref: exclusiveRatesRef, inView: exclusiveRatesInView } = useInView({
    threshold: 0.3,
    triggerOnce: false,
  });

  // Negotiation animation refs and observers
  const negotiationLottieRef = useRef<LottieRefCurrentProps>(null);
  const [isNegotiationPlaying, setIsNegotiationPlaying] = useState(true);
  const { ref: negotiationRef, inView: negotiationInView } = useInView({
    threshold: 0.3,
    triggerOnce: false,
  });

  // All-in-one animation refs and observers
  const allInOneLottieRef = useRef<LottieRefCurrentProps>(null);
  const [isAllInOnePlaying, setIsAllInOnePlaying] = useState(true);
  const { ref: allInOneRef, inView: allInOneInView } = useInView({
    threshold: 0.3,
    triggerOnce: false,
  });

  // Progress tracking using custom hook
  const exclusiveRatesProgress = useLottieProgress(
    exclusiveRatesLottieRef,
    exclusiveRatesInView,
    isExclusiveRatesPlaying
  );
  const negotiationProgress = useLottieProgress(negotiationLottieRef, negotiationInView, isNegotiationPlaying);
  const allInOneProgress = useLottieProgress(allInOneLottieRef, allInOneInView, isAllInOnePlaying);

  useEffect(() => {
    if (exclusiveRatesInView && isExclusiveRatesPlaying) {
      exclusiveRatesLottieRef.current?.play();
    } else {
      exclusiveRatesLottieRef.current?.pause();
    }
  }, [exclusiveRatesInView, isExclusiveRatesPlaying]);

  useEffect(() => {
    if (negotiationInView && isNegotiationPlaying) {
      negotiationLottieRef.current?.play();
    } else {
      negotiationLottieRef.current?.pause();
    }
  }, [negotiationInView, isNegotiationPlaying]);

  useEffect(() => {
    if (allInOneInView && isAllInOnePlaying) {
      allInOneLottieRef.current?.play();
    } else {
      allInOneLottieRef.current?.pause();
    }
  }, [allInOneInView, isAllInOnePlaying]);

  const toggleExclusiveRatesPlayback = () => {
    setIsExclusiveRatesPlaying(!isExclusiveRatesPlaying);
  };

  const toggleNegotiationPlayback = () => {
    setIsNegotiationPlaying(!isNegotiationPlaying);
  };

  const toggleAllInOnePlayback = () => {
    setIsAllInOnePlaying(!isAllInOnePlaying);
  };

  return (
    <div className="w-full flex justify-center items-center py-12 md:py-24 relative" data-section="landing-feature3">
      <div className="w-full flex flex-col gap-8 md:gap-16">
        {/* Top Headings */}
        <div className="flex flex-col gap-4 md:gap-8 max-w-[960px] w-full mx-auto text-left md:text-center">
          <div className="flex flex-col justify-start md:justify-center items-start md:items-center gap-3 md:gap-6">
            <div className="text-h2 text-landing-primary">Save money, every time</div>
            <div className="max-w-[530px] text-body-lg text-landing-secondary">
              Thanks to exclusive partnerships and automated negotiation, we unlock rates and concessions that you can't
              get on your own.
            </div>
          </div>
        </div>

        {/* Alternating Sections */}
        <div className="flex flex-col gap-8 w-full">
          {/* Section 1 - Left Content, Right Visual */}
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-16 w-full pb-8 md:pb-24">
            <div className="flex-1 flex flex-col gap-3 md:gap-4 order-2 md:order-1 max-w-[460px]">
              <div className="text-h3 text-landing-primary">Exclusive Nowadays rates</div>
              <div className="text-body text-landing-secondary">
                We're IATA-certified and book millions in events annually, giving us access to preferred rates and
                concessions typically reserved for high-volume clients. These negotiated deals often beat what's
                available online or through direct booking.
              </div>
            </div>
            <div className="w-full md:flex-1 flex justify-center md:order-2 order-1">
              <div
                ref={exclusiveRatesRef}
                className="w-full rounded-2xl bg-landing-secondary flex justify-center items-center relative"
              >
                <Lottie
                  lottieRef={exclusiveRatesLottieRef}
                  animationData={exclusiveRatesAnimation}
                  loop={true}
                  autoplay={false}
                  style={{ width: "100%", pointerEvents: "none" }}
                />
                <LottiePlayerButton
                  isPlaying={isExclusiveRatesPlaying}
                  progress={exclusiveRatesProgress}
                  onClick={toggleExclusiveRatesPlayback}
                  ariaLabel={isExclusiveRatesPlaying ? "Pause animation" : "Play animation"}
                />
              </div>
            </div>
          </div>

          {/* Section 2 - Right Content, Left Visual */}
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-16 w-full py-8 md:py-24">
            <div className="w-full md:flex-1 flex justify-center order-1">
              <div
                ref={negotiationRef}
                className="w-full rounded-2xl bg-landing-secondary flex justify-center items-center relative"
              >
                <Lottie
                  lottieRef={negotiationLottieRef}
                  animationData={negotiationAnimation}
                  loop={true}
                  autoplay={false}
                  style={{ width: "100%", pointerEvents: "none" }}
                />
                <LottiePlayerButton
                  isPlaying={isNegotiationPlaying}
                  progress={negotiationProgress}
                  onClick={toggleNegotiationPlayback}
                  ariaLabel={isNegotiationPlaying ? "Pause animation" : "Play animation"}
                />
              </div>{" "}
            </div>
            <div className="w-full md:flex-1 flex flex-col gap-3 md:gap-4 order-2 max-w-[460px]">
              <div className="text-h3 text-landing-primary">Smarter negotiations, better deals</div>
              <div className="text-body text-landing-secondary">
                Our AI compares and negotiates proposals in real time, creating competitive pressure that saves clients
                hundreds to thousands of dollars. Backed by our internal database of benchmarks and contracts, we know
                how to secure the best overall value—not just the lowest rate.
              </div>
            </div>
          </div>

          {/* Section 3 - Left Content, Right Visual */}
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-16 w-full pt-8 md:pt-24">
            <div className="w-full md:flex-1 flex flex-col gap-3 md:gap-4 order-2 md:order-1 max-w-[460px]">
              <div className="text-h3 text-landing-primary">Everything, in-house</div>
              <div className="text-body text-landing-secondary">
                We replace the need for outside planners or contractors by handling every step—faster and more
                cost-efficiently. That means fewer hours spent coordinating, and more time focused on what actually
                moves the needle.
              </div>
            </div>
            <div className="w-full md:flex-1 flex justify-center order-1 md:order-2">
              <div
                ref={allInOneRef}
                className="w-full rounded-2xl bg-landing-secondary flex justify-center items-center relative"
              >
                <Lottie
                  lottieRef={allInOneLottieRef}
                  animationData={allInOneAnimation}
                  loop={true}
                  autoplay={false}
                  style={{ width: "100%", pointerEvents: "none" }}
                />
                <LottiePlayerButton
                  isPlaying={isAllInOnePlaying}
                  progress={allInOneProgress}
                  onClick={toggleAllInOnePlayback}
                  ariaLabel={isAllInOnePlaying ? "Pause animation" : "Play animation"}
                />
              </div>{" "}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
